import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useSuppliers } from '../../hooks/useWmsSuppliers';
import type { Supplier, SupplierStatus } from '../../types';

interface SupplierSelectProps {
  value?: string; // supplier_id
  onSelect: (supplier: { id: string; name: string } | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** If true, shows all suppliers including inactive ones */
  showInactive?: boolean;
}

function getStatusColor(status: SupplierStatus): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'active':
      return 'default';
    case 'inactive':
      return 'secondary';
    case 'suspended':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function getStatusLabel(status: SupplierStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'inactive':
      return 'Inactive';
    case 'suspended':
      return 'Suspended';
    default:
      return status;
  }
}

export function SupplierSelect({
  value,
  onSelect,
  placeholder = 'Select supplier...',
  disabled = false,
  className,
  showInactive = false,
}: SupplierSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch suppliers - use a large page size to get all
  const { data, isLoading } = useSuppliers({
    page_length: 100,
    status: showInactive ? undefined : 'active',
  });

  const suppliers = data?.suppliers ?? [];

  // Filter suppliers based on search query
  const filteredSuppliers = useMemo(() => {
    if (!searchQuery) return suppliers;
    const query = searchQuery.toLowerCase();
    return suppliers.filter(
      (supplier) =>
        supplier.name.toLowerCase().includes(query) ||
        supplier.account_number.toLowerCase().includes(query)
    );
  }, [suppliers, searchQuery]);

  // Find selected supplier
  const selectedSupplier = useMemo(
    () => suppliers.find((s) => s.id === value),
    [suppliers, value]
  );

  const handleSelect = (supplier: Supplier) => {
    if (supplier.id === value) {
      // Deselect if clicking the same supplier
      onSelect(null);
    } else {
      onSelect({ id: supplier.id, name: supplier.name });
    }
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
    setSearchQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selectedSupplier && 'text-muted-foreground',
            className
          )}
        >
          <span className="flex items-center gap-2 truncate">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : selectedSupplier ? (
              <>
                <span className="truncate">{selectedSupplier.name}</span>
                <Badge
                  variant={getStatusColor(selectedSupplier.status)}
                  className="ml-1 text-xs px-1.5"
                >
                  {getStatusLabel(selectedSupplier.status)}
                </Badge>
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search suppliers..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Loading suppliers...' : 'No supplier found.'}
            </CommandEmpty>
            <CommandGroup>
              {/* Clear option */}
              {value && (
                <CommandItem
                  onSelect={() => handleClear({} as React.MouseEvent)}
                  className="text-muted-foreground"
                >
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  Clear selection
                </CommandItem>
              )}
              {filteredSuppliers.map((supplier) => (
                <CommandItem
                  key={supplier.id}
                  value={supplier.id}
                  onSelect={() => handleSelect(supplier)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === supplier.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex items-center justify-between w-full">
                    <div className="flex flex-col">
                      <span className="font-medium">{supplier.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {supplier.account_number}
                      </span>
                    </div>
                    <Badge
                      variant={getStatusColor(supplier.status)}
                      className="ml-2 text-xs"
                    >
                      {getStatusLabel(supplier.status)}
                    </Badge>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
